import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const findStepTarget = (step) => {
  if (step.target) {
    const nodes = Array.from(document.querySelectorAll(step.target));
    if (!nodes.length) {
      console.warn('[Explainer] target not found:', step.target);
      return null;
    }
    // Un même data-explain peut exister en version mobile ET desktop (ex. le
    // sélecteur de bien). On ne garde que les occurrences réellement visibles
    // (largeur/hauteur > 0) puis on applique l'index demandé, sinon l'étape
    // se positionne sur le doublon mobile invisible (rect nul → rien n'est
    // surligné ou le highlight reste sur l'étape précédente).
    const visible = nodes.filter((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });
    const pool = visible.length ? visible : nodes;
    const el = pool[step.index || 0];
    if (!el) console.warn('[Explainer] target index out of range:', step.target, step.index || 0);
    return el || null;
  }
  if (step.h2Text) {
    const allH2 = document.querySelectorAll("h2");
    for (const h2 of allH2) {
      if (h2.textContent.trim() === step.h2Text.trim()) {
        const wrapper = h2.closest(".dashboard-section-dnd-wrapper") || h2.closest(".dashboard-section") || h2;
        console.log('[Explainer] h2Text match:', step.h2Text, '→ wrapper:', wrapper.className, 'rect:', wrapper.getBoundingClientRect());
        return wrapper;
      }
    }
    console.warn('[Explainer] h2Text NOT FOUND:', step.h2Text, '— available h2:', Array.from(allH2).map(h => h.textContent.trim()));
    // fallback: use data-section-id
    const fallbackKeys = { "Learning Center": "trainingCenter", "New content in the real estate training center": "trainingCenter" };
    for (const [key, sectionId] of Object.entries(fallbackKeys)) {
      if (step.h2Text.includes(key)) {
        const fb = document.querySelector(`[data-section-id="${sectionId}"]`);
        if (fb) { console.log('[Explainer] fallback found:', sectionId); return fb; }
      }
    }
    return null;
  }
  return null;
};

const ExplainerTour = ({ steps, onFinish, onClose, showCheckbox, storageKey, vertical = false }) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, placement: "bottom" });
  const [targetRect, setTargetRect] = useState(null);
  const [panelH, setPanelH] = useState(190);
  const stepRef = useRef(null);
  const overlayRef = useRef(null);

  const step = steps[current];

  const getSidebarWidth = () => {
    const sidebar = document.querySelector('.sidebar-container');
    if (!sidebar) return 70;
    return sidebar.getBoundingClientRect().width;
  };

  const updatePosition = useCallback(() => {
    if (!step) return;
    const el = findStepTarget(step);
    // Cible introuvable ou non rendue : on vide le trou du masque pour ne pas
    // laisser le highlight sur l'élément de l'étape précédente (bug « étape 2 =
    // toujours surlignée comme l'étape 1 »).
    if (!el) { setTargetRect(null); return; }
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) { setTargetRect(null); return; }
    setTargetRect(rect);

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 16;
    const gap = 12;
    const sidebarW = getSidebarWidth();
    const minLeft = sidebarW + margin;
    let top, left, placement;

    if (vertical) {
      const tooltipW = Math.min(360, vw - margin * 2);
      const tooltipH = panelH;
      const spaceAbove = rect.top - gap - margin;
      const spaceBelow = vh - rect.bottom - gap - margin;
      const isSmall = vw < 768;

      if (isSmall) {
        const nearTop = rect.top < vh * 0.35;
        const nearBottom = rect.bottom > vh * 0.65;
        if (nearTop && spaceBelow >= tooltipH) placement = "bottom";
        else if (nearBottom && spaceAbove >= tooltipH) placement = "top";
        else placement = spaceBelow >= spaceAbove ? "bottom" : "top";
      } else if (spaceAbove >= tooltipH) {
        placement = "top";
      } else if (spaceBelow >= tooltipH) {
        placement = "bottom";
      } else {
        placement = spaceBelow >= spaceAbove ? "bottom" : "top";
      }

      top = placement === "top" ? rect.top - gap - tooltipH : rect.bottom + gap;

      const desiredLeft = rect.left + rect.width / 2 - tooltipW / 2;
      const maxLeft = Math.max(margin, vw - tooltipW - margin);
      left = Math.max(margin, Math.min(desiredLeft, maxLeft));
      let arrowOffset = rect.left + rect.width / 2 - left - 10;
      arrowOffset = Math.max(14, Math.min(arrowOffset, tooltipW - 34));
      top = Math.max(margin, Math.min(top, vh - tooltipH - margin));

      setTooltipPos({ top, left, placement, arrowOffset, width: tooltipW });
      return;
    }

    const tooltipW = 360;
    const tooltipH = 180;

    const fitsRight = rect.right + tooltipW + 16 < vw;
    const fitsLeft = rect.left - tooltipW - 16 > minLeft;
    const fitsBottom = rect.bottom + tooltipH + 16 < vh;
    const fitsTop = rect.top - tooltipH - 16 > 0;

    if (fitsRight) {
      placement = "right";
      top = Math.min(Math.max(rect.top + rect.height / 2 - tooltipH / 2, 16), vh - tooltipH - 16);
      left = Math.max(rect.right + 12, minLeft);
    } else if (fitsLeft) {
      placement = "left";
      top = Math.min(Math.max(rect.top + rect.height / 2 - tooltipH / 2, 16), vh - tooltipH - 16);
      left = Math.max(rect.left - tooltipW - 12, minLeft);
    } else if (fitsBottom) {
      placement = "bottom";
      top = rect.bottom + 12;
      left = Math.max(Math.min(rect.left + rect.width / 2 - tooltipW / 2, vw - tooltipW - 16), minLeft);
    } else if (fitsTop) {
      placement = "top";
      top = rect.top - tooltipH - 12;
      left = Math.max(Math.min(rect.left + rect.width / 2 - tooltipW / 2, vw - tooltipW - 16), minLeft);
    } else {
      placement = "bottom";
      top = Math.min(rect.bottom + 12, vh - tooltipH - 16);
      left = Math.max(rect.left, minLeft);
    }

    setTooltipPos({ top, left, placement, arrowOffset: null, width: tooltipW });
  }, [step, vertical, panelH]);

  useEffect(() => {
    if (!step) return;
    const el = findStepTarget(step);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });

    let lastRectStr = '';
    const stabilizers = [100, 200, 400, 800, 1200, 1800, 2600];
    let timer;

    const tryPosition = (retries = 0) => {
      const found = findStepTarget(step);
      if (found && found.getBoundingClientRect().height > 0) {
        updatePosition();
        const r = found.getBoundingClientRect();
        const rs = `${r.left},${r.top},${r.width},${r.height}`;
        if (rs !== lastRectStr) {
          lastRectStr = rs;
          if (retries < stabilizers.length) {
            timer = setTimeout(() => tryPosition(retries + 1), stabilizers[retries]);
          }
        }
      } else if (retries < 8) {
        timer = setTimeout(() => tryPosition(retries + 1), 250);
      }
    };

    requestAnimationFrame(() => {
      setTimeout(() => tryPosition(), 50);
    });
    window.addEventListener("resize", updatePosition);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updatePosition);
    };
  }, [step, updatePosition]);

  useEffect(() => {
    if (!step) return;
    const id = requestAnimationFrame(() => {
      if (stepRef.current && stepRef.current.offsetHeight > 0 && Math.abs(stepRef.current.offsetHeight - panelH) > 1) {
        setPanelH(stepRef.current.offsetHeight);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [step, panelH, tooltipPos]);

  const handleNext = () => {
    if (current < steps.length - 1) {
      setCurrent(current + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (current > 0) setCurrent(current - 1);
  };

  const handleFinish = () => {
    if (dontShowAgain) {
      try { localStorage.setItem(storageKey, "true"); } catch (e) { /* ignore */ }
    }
    if (onFinish) onFinish(dontShowAgain);
    if (onClose) onClose(dontShowAgain);
  };

  const handleSkip = () => {
    if (dontShowAgain) {
      try { localStorage.setItem(storageKey, "true"); } catch (e) { /* ignore */ }
    }
    if (onClose) onClose(dontShowAgain);
  };

  if (!step) return null;

  const arrowSize = 10;
  const bc = "var(--brand,#976DD0)";
  const arrowOffset = tooltipPos.arrowOffset;
  const horizontalArrow = arrowOffset != null
    ? { left: arrowOffset, marginLeft: 0 }
    : { left: "50%", marginLeft: -arrowSize };
  const arrowStyle = (() => {
    switch (tooltipPos.placement) {
      case "top": return { bottom: -arrowSize, ...horizontalArrow, borderLeft: `${arrowSize}px solid transparent`, borderRight: `${arrowSize}px solid transparent`, borderTop: `${arrowSize}px solid ${bc}` };
      case "bottom": return { top: -arrowSize, ...horizontalArrow, borderLeft: `${arrowSize}px solid transparent`, borderRight: `${arrowSize}px solid transparent`, borderBottom: `${arrowSize}px solid ${bc}` };
      case "right": return { left: -arrowSize, top: "50%", marginTop: -arrowSize, borderTop: `${arrowSize}px solid transparent`, borderBottom: `${arrowSize}px solid transparent`, borderRight: `${arrowSize}px solid ${bc}` };
      case "left": return { right: -arrowSize, top: "50%", marginTop: -arrowSize, borderTop: `${arrowSize}px solid transparent`, borderBottom: `${arrowSize}px solid transparent`, borderLeft: `${arrowSize}px solid ${bc}` };
      default: return {};
    }
  })();

  const panelStyle = {
    position: "fixed",
    zIndex: 100000,
    top: tooltipPos.top,
    left: tooltipPos.left,
    width: tooltipPos.width || 360,
    maxWidth: "calc(100vw - 32px)",
    pointerEvents: "auto",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 8px 40px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.1)",
    padding: "24px 24px 16px",
  };

  const panelContent = (
    <>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brand,#976DD0)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
        {t("explainer.step")} {current + 1}/{steps.length}
      </div>

      <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a2e", margin: "0 0 6px", lineHeight: 1.4 }}>
        {step.title}
      </h3>

      <p style={{ fontSize: 14, color: "#555", margin: "0 0 18px", lineHeight: 1.6 }}>
        {step.description}
      </p>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        {showCheckbox ? (
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: "#999", userSelect: "none" }}>
            <input type="checkbox" checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              style={{ accentColor: "var(--brand,#976DD0)", width: 14, height: 14 }} />
            {t("explainer.dontShowAgain")}
          </label>
        ) : <div />}

        <div style={{ display: "flex", gap: 6 }}>
          {current > 0 && (
            <button onClick={handlePrev} style={btnStyle.secondary}>
              {t("explainer.previous")}
            </button>
          )}
          <button onClick={handleNext} style={btnStyle.primary}>
            {current < steps.length - 1 ? t("explainer.next") : t("explainer.finish")}
          </button>
        </div>
      </div>

      <div style={{ textAlign: "right", marginTop: 8 }}>
        <button onClick={handleSkip} style={{ ...btnStyle.link, fontSize: 13 }}>
          {t("explainer.skip")}
        </button>
      </div>
    </>
  );

  return (
    <div ref={overlayRef} style={{
      position: "fixed", inset: 0, zIndex: 99999,
      pointerEvents: "none",
    }}>
      <svg width="100%" height="100%" style={{ position: "absolute", top: 0, left: 0 }}>
        <defs>
          <mask id="explainer-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect x={targetRect.left - 4} y={targetRect.top - 4}
                width={targetRect.width + 8} height={targetRect.height + 8}
                rx="8" ry="8" fill="black" />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#explainer-mask)" style={{ pointerEvents: "auto" }} />
      </svg>

      <div ref={stepRef} style={panelStyle}>
        <div style={{ position: "absolute", ...arrowStyle }} />
        {panelContent}
      </div>
    </div>
  );
};

const btnStyle = {
  primary: {
    background: "var(--brand,#976DD0)", color: "#fff", border: "none", borderRadius: 20,
    padding: "8px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  secondary: {
    background: "var(--brand-pale,#F0E6FF)", color: "var(--brand,#976DD0)", border: "none", borderRadius: 20,
    padding: "8px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  link: {
    background: "none", border: "none", color: "#999", cursor: "pointer",
    textDecoration: "underline", fontSize: 13,
  },
};

export default ExplainerTour;
